/**
 * Syllabus Uploader Component
 * Drag-and-drop file upload with progress tracking
 */

import { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  uploadSyllabus,
  uploadMultipleSyllabusImages,
  validateSyllabusFile,
  validateBatchFiles,
  formatFileSize,
  type UploadProgress,
} from '@/lib/syllabus-upload';
import type { ParsedSyllabusData } from '@/types/syllabus';
import {
  Upload,
  FileUp,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';

interface SyllabusUploaderProps {
  courseId?: string;
  onSuccess?: (syllabusId: string, parsedData?: ParsedSyllabusData) => void;
  onError?: (error: string) => void;
  className?: string;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export function SyllabusUploader({
  courseId,
  onSuccess,
  onError,
  className,
}: SyllabusUploaderProps) {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // For multi-image
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const multiFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    const validation = validateSyllabusFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      setUploadState('error');
      onError?.(validation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
    setSelectedFiles([]); // Clear multi-file selection
    setError(null);
    setUploadState('idle');
  }, [onError]);

  const handleMultiFileSelect = useCallback((files: File[]) => {
    const validation = validateBatchFiles(files);
    if (!validation.valid) {
      setError(validation.error || 'Invalid files');
      setUploadState('error');
      onError?.(validation.error || 'Invalid files');
      return;
    }

    setSelectedFiles(files);
    setSelectedFile(null); // Clear single-file selection
    setError(null);
    setUploadState('idle');
  }, [onError]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);

      // Check if multiple images were dropped
      const allImages = droppedFiles.every((f) => {
        const ext = f.name.split('.').pop()?.toLowerCase();
        return ext && ['png', 'jpg', 'jpeg', 'webp', 'heic'].includes(ext);
      });

      if (droppedFiles.length > 1 && allImages) {
        // Multi-image mode
        handleMultiFileSelect(droppedFiles);
      } else if (droppedFiles.length === 1) {
        // Single file mode
        handleFileSelect(droppedFiles[0]);
      } else {
        // Mixed file types with multiple files - not supported
        setError('Multiple files must all be images (PNG, JPG, WEBP, HEIC)');
        setUploadState('error');
      }
    },
    [handleFileSelect, handleMultiFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleMultiInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        handleMultiFileSelect(files);
      }
    },
    [handleMultiFileSelect]
  );

  const handleUpload = async () => {
    if (!selectedFile && selectedFiles.length === 0) return;

    setUploadState('uploading');
    setError(null);

    let result;
    if (selectedFiles.length > 0) {
      // Multi-file upload
      result = await uploadMultipleSyllabusImages(selectedFiles, courseId, (p) => {
        setProgress(p);
      });
    } else if (selectedFile) {
      // Single file upload
      result = await uploadSyllabus(selectedFile, courseId, (p) => {
        setProgress(p);
      });
    } else {
      return;
    }

    if (result.success && result.syllabusId) {
      setUploadState('success');
      onSuccess?.(result.syllabusId, result.parsedData);
    } else {
      setUploadState('error');
      setError(result.error || 'Upload failed');
      onError?.(result.error || 'Upload failed');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setUploadState('idle');
    setProgress(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
    }
  };

  const getFileIcon = () => {
    if (!selectedFile) return <FileUp className="h-8 w-8 text-muted-foreground" />;

    const extension = selectedFile.name.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    if (extension === 'docx' || extension === 'doc') {
      return <FileText className="h-8 w-8 text-blue-500" />;
    }
    if (['png', 'jpg', 'jpeg', 'webp', 'heic'].includes(extension || '')) {
      return <ImageIcon className="h-8 w-8 text-green-500" />;
    }
    return <FileText className="h-8 w-8 text-muted-foreground" />;
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-5 w-5 text-primary" />
          Upload Syllabus
        </CardTitle>
      </CardHeader>

      <CardContent>
        {/* Drop zone */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Drop zone for syllabus file upload. Click or drag and drop a PDF, DOC, DOCX, TXT, or image file."
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className={cn(
            'relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer',
            isDragging && 'border-primary bg-primary/5',
            !isDragging && 'border-muted-foreground/25 hover:border-primary/50',
            uploadState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20',
            uploadState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20'
          )}
          onClick={() => fileInputRef.current?.click()}
        >
          {/* Single file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp,.heic"
            onChange={handleInputChange}
            className="hidden"
          />
          {/* Multi-file input (images only) */}
          <input
            ref={multiFileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.webp,.heic"
            multiple
            onChange={handleMultiInputChange}
            className="hidden"
          />

          {/* Idle state - no files selected */}
          {uploadState === 'idle' && !selectedFile && selectedFiles.length === 0 && (
            <div className="text-center">
              <FileUp className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">
                Drop your syllabus here or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports PDF, DOC, DOCX, TXT, and images (max 50MB)
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    multiFileInputRef.current?.click();
                  }}
                >
                  <ImageIcon className="h-4 w-4 mr-1" />
                  Multiple Photos
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Taking photos of a printed syllabus? Select multiple images.
              </p>
            </div>
          )}

          {/* Single file selected */}
          {uploadState === 'idle' && selectedFile && (
            <div className="flex items-center gap-4">
              {getFileIcon()}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReset();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Multiple files selected */}
          {uploadState === 'idle' && selectedFiles.length > 0 && (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium">
                    {selectedFiles.length} images selected
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                >
                  Clear All
                </Button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between py-1 px-2 bg-muted/50 rounded text-xs"
                  >
                    <span className="truncate flex-1">{file.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {formatFileSize(file.size)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Total: {formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))}
              </p>
            </div>
          )}

          {/* Uploading state */}
          {uploadState === 'uploading' && progress && (
            <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {progress.message || 'Processing...'}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {progress.phase}
                  </p>
                </div>
                <span className="text-sm font-medium">{progress.progress}%</span>
              </div>
              <Progress value={progress.progress} className="h-2" />
            </div>
          )}

          {/* Success state */}
          {uploadState === 'success' && (
            <div
              className="flex items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  Syllabus uploaded successfully!
                </p>
                <p className="text-xs text-muted-foreground">
                  Your syllabus has been analyzed and saved.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleReset}>
                Upload Another
              </Button>
            </div>
          )}

          {/* Error state */}
          {uploadState === 'error' && (
            <div
              className="flex items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertCircle className="h-8 w-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Upload failed
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-400/80">
                  {error}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleReset}>
                Try Again
              </Button>
            </div>
          )}
        </div>

        {/* Upload button */}
        {uploadState === 'idle' && (selectedFile || selectedFiles.length > 0) && (
          <Button onClick={handleUpload} className="w-full mt-4">
            <Upload className="h-4 w-4 mr-2" />
            {selectedFiles.length > 0
              ? `Upload & Analyze ${selectedFiles.length} Images`
              : 'Upload & Analyze'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default SyllabusUploader;
